import { useState, useCallback, useEffect } from "react";
import EmailTemplateService from "../services/EmailTemplate";
import EmailFooterService from "../services/EmailFooters";
import EmailSignatureService from "../services/EmailSignatures";
import { NULL_TEMPLATE } from "../constants/Constants";
import { decodeHtml, createTemplateOptions } from "../helpers/emailHelper";

/**
 * Custom hook for managing email templates, footers, and signatures
 * @param {Object} options
 * @param {Function} options.templateDisabledFn - Function to check if template is disabled
 * @param {Array} options.campaignTemplates - Array of campaign template IDs
 * @returns {Object} Template state and handlers
 */
export const useEmailTemplate = ({
  templateDisabledFn,
  campaignTemplates = [],
}) => {
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState({ ...NULL_TEMPLATE });
  const [templateOptions, setTemplateOptions] = useState([]);
  const [footerOptions, setFooterOptions] = useState([]);
  const [footerId, setFooterId] = useState(null);
  const [footerHtml, setFooterHtml] = useState("");
  const [signatureOptions, setSignatureOptions] = useState([]);
  const [signatureId, setSignatureId] = useState(null);
  const [signatureHtml, setSignatureHtml] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");

  const fetchTemplates = useCallback(async () => {
    try {
      let templates = await EmailTemplateService.getEmailTemplatesForFilters();
      templates = templates.map(template => ({
        id: template.id,
        key: template.id,
        value: template.id,
        text: template.name,
        name: template.name,
        subject: template.subject,
        html: template.html,
        info: template.info,
        enabled: template.enabled,
      }));
      setTemplates(templates);
    } catch (error) {
      console.error("Error fetching templates:", error);
      setTemplates([]);
    }
  }, []);

  const fetchEmailFooters = useCallback(async () => {
    try {
      let footers = await EmailFooterService.getEmailFootersForFilters();
      let footerOptions = footers.map(footer => ({
        key: footer.id,
        value: footer.id,
        text: footer.name,
      }));
      footerOptions.push({ key: null, value: null, text: "None" });
      setFooterOptions(footerOptions);
    } catch (error) {
      console.error("Error fetching email footers:", error);
      setFooterOptions([{ key: null, value: null, text: "None" }]);
    }
  }, []);

  const fetchEmailSignatures = useCallback(async () => {
    try {
      let signatures =
        await EmailSignatureService.getEmailSignaturesForFilters();
      let signatureOptions = signatures.map(signature => ({
        key: signature.id,
        value: signature.id,
        text: signature.name,
      }));
      signatureOptions.unshift({ key: null, value: null, text: "None" });
      setSignatureOptions(signatureOptions);
    } catch (error) {
      console.error("Error fetching email signatures:", error);
      setSignatureOptions([{ key: null, value: null, text: "None" }]);
    }
  }, []);

  const handleTemplateChange = useCallback(
    async (e, { value }) => {
      try {
        let selectedTemplate = templates.find(
          template => template.id === value
        );

        if (!selectedTemplate && value === null) {
          setTemplate({ ...NULL_TEMPLATE });
          return;
        }

        if (selectedTemplate && !selectedTemplate.html) {
          try {
            const fullTemplate = await EmailTemplateService.getEmailTemplate(
              value
            );
            selectedTemplate = {
              ...selectedTemplate,
              ...fullTemplate,
            };

            const updatedTemplates = [...templates];
            const idx = updatedTemplates.findIndex(t => t.id === value);
            if (idx !== -1) {
              updatedTemplates[idx] = selectedTemplate;
              setTemplates(updatedTemplates);
            }
          } catch (error) {
            console.error("Error fetching template:", error);
            selectedTemplate = { ...NULL_TEMPLATE };
          }
        }

        setTemplate(selectedTemplate || { ...NULL_TEMPLATE });
      } catch (error) {
        console.error("Error in handleTemplateChange:", error);
        setTemplate({ ...NULL_TEMPLATE });
      }
    },
    [templates]
  );

  const handleFooterChange = useCallback(async (e, { value }) => {
    let footerId = value;
    let footerHtml;
    try {
      if (!!footerId) {
        footerHtml = (await EmailFooterService.getEmailFooter(footerId)).html;
      } else {
        footerHtml = null;
      }
      setFooterHtml(footerHtml);
      setFooterId(footerId);
    } catch (error) {
      console.error("Error fetching footer:", error);
    }
  }, []);

  const handleSignatureChange = useCallback(async (e, { value }) => {
    let signatureId = value;
    let signatureHtml;
    try {
      if (!!signatureId) {
        signatureHtml = (
          await EmailSignatureService.getEmailSignature(signatureId)
        ).html;
      } else {
        signatureHtml = null;
      }
      setSignatureHtml(signatureHtml);
      setSignatureId(signatureId);
    } catch (error) {
      console.error("Error fetching signature:", error);
    }
  }, []);

  // Update template options when templates or campaign templates change
  useEffect(() => {
    if (templateDisabledFn) {
      const newTemplateOptions = createTemplateOptions(
        templates,
        campaignTemplates,
        templateDisabledFn
      );
      setTemplateOptions(newTemplateOptions);
    }
  }, [templates, campaignTemplates, templateDisabledFn]);

  // Update preview when template, footer, or signature changes
  useEffect(() => {
    if (!template?.html) {
      setPreviewHtml("");
      return;
    }

    let templateHtml = decodeHtml(template.html);
    let previewHtml;
    let before = templateHtml.indexOf("</body>");

    if (before === -1) {
      templateHtml = `<html><head></head><body>${templateHtml}</body></html>`;
      before = templateHtml.indexOf("</body>");
    }

    let footer = footerHtml ? decodeHtml(footerHtml) : "";
    let signature = signatureHtml ? decodeHtml(signatureHtml) : "";

    previewHtml =
      templateHtml.substring(0, before) +
      signature +
      footer +
      templateHtml.substring(before);

    setPreviewHtml(previewHtml);
  }, [template, footerHtml, signatureHtml]);

  return {
    // State
    templates,
    template,
    templateOptions,
    footerOptions,
    footerId,
    footerHtml,
    signatureOptions,
    signatureId,
    signatureHtml,
    previewHtml,

    // Handlers
    handleTemplateChange,
    handleFooterChange,
    handleSignatureChange,
    fetchTemplates,
    fetchEmailFooters,
    fetchEmailSignatures,

    // Setters (for advanced use cases)
    setTemplate,
    setTemplates,
  };
};
